import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import { supabase } from '../../supabase-client'

/**
 * XeroAuthCallback Component
 *
 * Handles OAuth redirect from Xero after user authorizes access.
 * Exchanges authorization code for tokens and saves to database.
 *
 * Expected URL format:
 * /xero/callback?code=AUTHORIZATION_CODE&state=STATE_VALUE
 *
 * Error URL format:
 * /xero/callback?error=access_denied&error_description=...
 *
 * @param {Object} props
 * @param {Object} props.user - Current authenticated user from Supabase
 */
export default function XeroAuthCallback({ user }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState('processing') // 'processing' | 'success' | 'error'
  const [message, setMessage] = useState('Processing authorization...')
  const [error, setError] = useState(null)
  const hasCalledCallback = useRef(false)

  useEffect(() => {
    // Prevent duplicate execution in React Strict Mode (development)
    // OAuth authorization codes are single-use only
    if (hasCalledCallback.current) return
    hasCalledCallback.current = true

    handleCallback()
  }, [])

  /**
   * Handle OAuth callback from Xero
   * Sends code to Edge Function to complete authorization
   */
  const handleCallback = async () => {
    // Check for error from Xero
    const errorParam = searchParams.get('error')
    if (errorParam) {
      const errorDescription = searchParams.get('error_description') || 'Authorization was denied'
      setStatus('error')
      setError(errorDescription)
      setMessage('Authorization failed')
      return
    }

    // Get authorization code
    const code = searchParams.get('code')
    const state = searchParams.get('state')

    if (!code) {
      setStatus('error')
      setError('No authorization code received from Xero')
      setMessage('Authorization failed')
      return
    }

    try {
      setMessage('Exchanging authorization code...')

      // Get session for auth header
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        setStatus('error')
        setError('Authentication required. Please log in again.')
        setMessage('Authorization failed')
        return
      }

      const { data, error } = await supabase.functions.invoke('xero-oauth', {
        body: {
          action: 'callback',
          code,
          state
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to complete authorization')
      }

      // Success!
      setStatus('success')
      setMessage('Successfully connected to Xero!')

      // Redirect to settings after 2 seconds with success flag
      setTimeout(() => {
        navigate('/farm-settings?tab=integrations&xero_connected=true')
      }, 2000)

    } catch (err) {
      console.error('[XeroCallback] Error:', err)
      setStatus('error')
      setError(err.message || 'Failed to complete authorization')
      setMessage('Authorization failed')
    }
  }

  const handleRetry = () => {
    navigate('/farm-settings?tab=integrations')
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>Xero Authorization</CardTitle>
          <CardDescription>
            {status === 'processing' && 'Please wait while we complete the connection...'}
            {status === 'success' && 'Connection established successfully'}
            {status === 'error' && 'There was a problem connecting to Xero'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Processing State */}
          {status === 'processing' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground text-center">{message}</p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
              <p className="text-sm text-muted-foreground text-center">{message}</p>
              <p className="text-xs text-muted-foreground">Redirecting to settings...</p>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="flex flex-col items-center gap-4 py-8">
                <XCircle className="w-12 h-12 text-red-600" />
                <p className="text-sm font-medium text-center">{message}</p>
              </div>

              <Alert variant="destructive">
                <AlertDescription>
                  <p className="font-medium mb-2">Error Details:</p>
                  <p className="text-sm">{error}</p>
                  <p className="text-sm mt-4">
                    If this problem persists, please contact support at{' '}
                    <a
                      href="mailto:support@caledoniamushrooms.com"
                      className="underline"
                    >
                      support@caledoniamushrooms.com
                    </a>
                  </p>
                </AlertDescription>
              </Alert>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleRetry}
                  className="flex-1"
                >
                  Go to Settings
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  className="flex-1"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
